import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import {
  Loader2, CheckCircle2, Eye, Globe, MapPin, Mail, Phone, Download,
  Send, RotateCcw, Copy, MailCheck,
} from "lucide-react";
import { toast } from "sonner";
import ProspectDetailPanel from "@/admin/ProspectDetailPanel";

function formatSentDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      timeZone: "Indian/Reunion",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminReady() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sendingBatch, setSendingBatch] = useState(false);

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get("/admin/prospects?status=ready_to_send&limit=200");
        if (cancelled) return;
        setItems(data.items);
      } catch (e) {
        if (!cancelled) toast.error("Impossible de charger les prospects prêts.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const onRevert = async (id, e) => {
    e?.stopPropagation();
    setUpdatingId(id);
    try {
      await adminApi.patch(`/admin/prospects/${id}/status`, { status: "pending_validation" });
      toast.success("Renvoyé en validation.");
      triggerReload();
    } catch { toast.error("Action échouée."); }
    finally { setUpdatingId(null); }
  };

  const onCopyEmail = async (p, e) => {
    e?.stopPropagation();
    const text = `Objet : ${p.email_subject || ""}\n\n${p.email_draft || ""}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Email « ${p.nom_entreprise} » copié.`);
    } catch { toast.error("Copie impossible."); }
  };

  const onSendOne = async (p, e) => {
    e?.stopPropagation();
    if (!p.email_contact) {
      toast.error("Pas d'email destinataire pour ce prospect.");
      return;
    }
    if (p.sent_at) {
      if (!window.confirm("Cet email a déjà été envoyé. Renvoyer ?")) return;
    } else {
      if (!window.confirm(`Envoyer maintenant à ${p.email_contact} ?`)) return;
    }
    setSendingId(p.id);
    try {
      const url = p.sent_at ? `/admin/prospects/${p.id}/send?force=true` : `/admin/prospects/${p.id}/send`;
      await adminApi.post(url);
      toast.success(`Email envoyé à ${p.email_contact}.`);
      triggerReload();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Envoi échoué.");
    } finally {
      setSendingId(null);
    }
  };

  const onSendBatch = async () => {
    const pending = items.filter((p) => !p.sent_at && p.email_contact);
    if (pending.length === 0) {
      toast.info("Aucun email à envoyer (déjà tous envoyés ou pas d'email destinataire).");
      return;
    }
    if (!window.confirm(`Envoyer ${pending.length} email(s) en une fois ?\n(Resend rate-limit 2/s, ~${Math.ceil(pending.length * 0.7)}s estimé)`)) return;
    setSendingBatch(true);
    try {
      const { data } = await adminApi.post("/admin/prospects/send-batch");
      const lines = [];
      if (data.sent) lines.push(`✓ ${data.sent} envoyés`);
      if (data.skipped) lines.push(`· ${data.skipped} ignorés`);
      if (data.errors) lines.push(`⚠ ${data.errors} erreurs`);
      toast.success(lines.join(" "));
      const failed = (data.details || []).filter((d) => d.status === "error");
      if (failed.length) toast.warning(`Erreurs : ${failed.slice(0, 2).map((f) => `${f.nom} (${f.reason})`).join(" · ")}`);
      triggerReload();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Envoi batch échoué.");
    } finally {
      setSendingBatch(false);
    }
  };

  const onExportCsv = async () => {
    if (items.length === 0) { toast.info("Aucun prospect à exporter."); return; }
    setExporting(true);
    try {
      const res = await adminApi.get("/admin/prospects/export.csv?status=ready_to_send", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `em-prospects-pret-a-envoyer-${date}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`${items.length} prospect(s) exporté(s).`);
    } catch { toast.error("Export échoué."); }
    finally { setExporting(false); }
  };

  const pendingToSend = items.filter((p) => !p.sent_at && p.email_contact).length;

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl" data-testid="admin-ready">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="text-xs tracking-[2px] text-white/40 font-semibold">— PIPELINE</div>
          <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight inline-flex items-center gap-3">
            <CheckCircle2 size={26} className="text-emerald-400/70" /> Prêts à envoyer
            <span className="text-base text-white/45 font-light">({items.length})</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onSendBatch}
            disabled={sendingBatch || pendingToSend === 0}
            data-testid="ready-send-batch"
            title={pendingToSend === 0 ? "Tout est déjà envoyé" : `Envoyer ${pendingToSend} email(s)`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sendingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={14} />}
            {sendingBatch ? "Envoi…" : `Tout envoyer ${pendingToSend > 0 ? `(${pendingToSend})` : ""}`}
          </button>
          <button
            type="button"
            onClick={onExportCsv}
            disabled={exporting || items.length === 0}
            data-testid="ready-export-csv"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download size={14} />}
            {exporting ? "Export…" : "CSV"}
          </button>
        </div>
      </div>
      <p className="text-white/55 text-sm mb-6">
        Cliquez « Tout envoyer » pour expédier en masse via Resend, ou utilisez le bouton avion par ligne. Les réponses des prospects retombent dans contact@emgroupdigital.fr.
      </p>

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 flex items-center justify-center gap-2 text-white/40 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="ready-empty">
          <Send size={32} className="mx-auto text-white/20" />
          <div className="mt-3 text-sm text-white/65">Aucun prospect prêt à envoyer.</div>
          <div className="mt-1 text-xs text-white/40">
            Approuvez des prospects depuis la boîte de validation pour les voir apparaître ici.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ready-table">
              <thead className="text-[10px] uppercase tracking-[0.12em] text-white/40 border-b border-white/10">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Entreprise</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Objet</th>
                  <th className="text-left px-4 py-3 font-semibold">Envoi</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const sent = !!p.sent_at;
                  const noEmail = !p.email_contact;
                  return (
                    <tr
                      key={p.id}
                      onClick={() => setSelectedId(p.id)}
                      data-testid={`ready-row-${p.id}`}
                      className={`border-b border-white/5 last:border-b-0 transition cursor-pointer ${sent ? "bg-emerald-500/[0.03] hover:bg-emerald-500/[0.06]" : "hover:bg-white/[0.03]"}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">{p.nom_entreprise}</div>
                        <div className="mt-0.5 flex items-center gap-3 text-[11px] text-white/45 flex-wrap">
                          {p.ville && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {p.ville}</span>}
                          {p.site_web && (
                            <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
                              <Globe size={10} /> {p.site_web.replace(/^https?:\/\//, "")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-white/65">
                        {p.email_contact ? (
                          <div className="inline-flex items-center gap-1 text-[12px]"><Mail size={11} /> {p.email_contact}</div>
                        ) : p.telephone ? (
                          <div className="inline-flex items-center gap-1 text-[12px] text-amber-300/80" title="Pas d'email → envoi impossible"><Phone size={11} /> {p.telephone}</div>
                        ) : (
                          <span className="text-white/30">— pas de contact —</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-white/75 max-w-[280px]">
                        <div className="truncate">{p.email_subject || "—"}</div>
                      </td>
                      <td className="px-4 py-3" data-testid={`ready-sent-status-${p.id}`}>
                        {sent ? (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px]">
                            <MailCheck size={11} />
                            <span>Envoyé · {formatSentDate(p.sent_at)}</span>
                          </div>
                        ) : noEmail ? (
                          <span className="text-amber-300/80 text-[11px]">Pas d&apos;email</span>
                        ) : (
                          <span className="text-white/40 text-[11px]">En attente</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={(e) => onSendOne(p, e)}
                            disabled={sendingId === p.id || noEmail}
                            className={`p-2 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed ${
                              sent
                                ? "text-emerald-300/70 hover:text-emerald-200 hover:bg-emerald-500/10"
                                : "text-[#E52431] hover:text-white hover:bg-[#E52431]/20"
                            }`}
                            title={noEmail ? "Pas d'email destinataire" : (sent ? "Renvoyer" : "Envoyer maintenant")}
                            data-testid={`ready-send-${p.id}`}
                          >
                            {sendingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                          </button>
                          <button
                            onClick={(e) => onCopyEmail(p, e)}
                            className="p-2 rounded-lg text-white/45 hover:text-white hover:bg-white/5 transition"
                            title="Copier l'email"
                            data-testid={`ready-copy-${p.id}`}
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={(e) => onRevert(p.id, e)}
                            disabled={updatingId === p.id}
                            className="p-2 rounded-lg text-white/45 hover:text-amber-300 hover:bg-white/5 transition disabled:opacity-50"
                            title="Renvoyer en validation"
                            data-testid={`ready-revert-${p.id}`}
                          >
                            <RotateCcw size={14} />
                          </button>
                          <button
                            className="p-2 rounded-lg text-white/45 hover:text-white hover:bg-white/5 transition"
                            title="Voir détail"
                            data-testid={`ready-view-${p.id}`}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ProspectDetailPanel
        prospectId={selectedId}
        onClose={() => setSelectedId(null)}
        onRescored={triggerReload}
      />
    </div>
  );
}
